import React, { CSSProperties, KeyboardEvent, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Dayjs } from 'dayjs';
import { SmartScheduleCoach, SmartScheduleEvent, toSmartScheduleKst } from '@/features/smartSchedule/model';
import AgendaPanel from './AgendaPanel';
import { useCoachColorResolver } from './CoachColorContext';
import EventCard, { useSmartSchedulePreviewController } from './EventCard';
import { useQuickMoveController } from '../QuickMove/QuickMoveContext';
import type { QuickMoveDraft, QuickMoveSource } from '@/features/scheduleQuickMove';
import { findQuickMoveOverlaps } from '@/features/scheduleQuickMove/overlaps';
import { validateQuickMoveDraft } from '@/features/scheduleQuickMove/time';
import {
    formatSlotTime,
    getDisplayHours,
    getEventStart,
    getStatusGroup,
    isScheduleSlotFree,
    layoutOverlaps,
} from './utils';

type DayViewProps = {
    selectedDay: any;
    now: Dayjs;
    coaches: SmartScheduleCoach[];
    events: SmartScheduleEvent[];
    availabilityEvents?: SmartScheduleEvent[];
    onOpenEvent: (event: SmartScheduleEvent) => void;
    onCreateSlot: (slot: { date: string; startTime: string; coachId?: string; coachName?: string }) => void;
};

const MIN_HOUR_WIDTH = 84;
const DEFAULT_COACH_COLUMN_WIDTH = 112;
const NARROW_COACH_COLUMN_WIDTH = 104;
const NARROW_VIEWPORT_BREAKPOINT = 1440;
const UNASSIGNED_COACH_ID = '__unassigned__';

type DayDensity = 'spacious' | 'comfortable' | 'compact' | 'dense';

const DAY_DENSITY: Record<DayDensity, { minRowHeight: number; maxRowHeight: number }> = {
    spacious: { minRowHeight: 64, maxRowHeight: 88 },
    comfortable: { minRowHeight: 46, maxRowHeight: 72 },
    compact: { minRowHeight: 38, maxRowHeight: 52 },
    dense: { minRowHeight: 34, maxRowHeight: 40 },
};

const DayView = ({
    selectedDay,
    now,
    coaches,
    events,
    availabilityEvents = events,
    onOpenEvent,
    onCreateSlot,
}: DayViewProps) => {
    const quickMove = useQuickMoveController();
    const preview = useSmartSchedulePreviewController();
    const scrollRef = useRef<HTMLDivElement>(null);
    const headerScrollRef = useRef<HTMLDivElement>(null);
    const [agenda, setAgenda] = useState<{ title: string; events: SmartScheduleEvent[] } | null>(null);
    const [viewportWidth, setViewportWidth] = useState(0);
    const [rowViewportBudget, setRowViewportBudget] = useState(0);
    const [horizontalScrollMax, setHorizontalScrollMax] = useState(0);
    const [horizontalScrollLeft, setHorizontalScrollLeft] = useState(0);
    const [bottomRailBounds, setBottomRailBounds] = useState({ left: 0, width: 0 });
    const [drag, setDrag] = useState<{ eventId: string; coachId: string; source: QuickMoveSource; draft: QuickMoveDraft; top: number; outsideRow: boolean } | null>(null);
    const [dragNotice, setDragNotice] = useState('');
    const dragRef = useRef<{
        event: SmartScheduleEvent; source: QuickMoveSource; pointerId: number; anchor: HTMLElement;
        clientX: number; clientY: number; contentX: number; startMinute: number; rowTop: number; rowBottom: number;
        laneTop: number; active: boolean; draft: QuickMoveDraft; outsideRow: boolean;
    } | null>(null);
    const suppressedClick = useRef<{ id: string; until: number } | null>(null);
    const getCoachColor = useCoachColorResolver();
    const date = useMemo(() => toSmartScheduleKst(selectedDay).startOf('day'), [selectedDay]);
    const { startHour, endHour } = useMemo(() => getDisplayHours(events, [date]), [date, events]);
    const hourCount = endHour - startHour;
    const coachColumnWidth = viewportWidth > 0 && viewportWidth < NARROW_VIEWPORT_BREAKPOINT
        ? NARROW_COACH_COLUMN_WIDTH
        : DEFAULT_COACH_COLUMN_WIDTH;
    const hourWidth = Math.max(
        MIN_HOUR_WIDTH,
        viewportWidth > coachColumnWidth ? (viewportWidth - coachColumnWidth) / hourCount : 0
    );
    const timelineWidth = hourCount * hourWidth;
    const boardWidth = timelineWidth + coachColumnWidth;
    const hasHorizontalOverflow = horizontalScrollMax > 1;
    const bottomRailThumbWidth = viewportWidth > 0 && boardWidth > 0
        ? Math.max(48, Math.min(viewportWidth - 8, (viewportWidth / boardWidth) * viewportWidth))
        : 48;
    const pixelsPerMinute = hourWidth / 60;
    const hourTicks = useMemo(
        () => Array.from({ length: hourCount }, (_, index) => startHour + index),
        [hourCount, startHour]
    );
    const slotStarts = useMemo(
        () => Array.from({ length: hourCount * 2 }, (_, index) => startHour * 60 + index * 30),
        [hourCount, startHour]
    );
    const includesUnassigned = availabilityEvents.some((event) => !event.coachId);
    const displayedCoaches = useMemo(() => {
        if (!includesUnassigned) return coaches;
        return [
            ...coaches,
            { id: UNASSIGNED_COACH_ID, name: '강사 미지정', order: Number.MAX_SAFE_INTEGER },
        ] as SmartScheduleCoach[];
    }, [coaches, includesUnassigned]);
    const density: DayDensity = displayedCoaches.length <= 5
        ? 'spacious'
        : displayedCoaches.length >= 16
        ? 'dense'
        : displayedCoaches.length >= 12
        ? 'compact'
        : 'comfortable';
    const densityRange = DAY_DENSITY[density];
    const fittedRowHeight = rowViewportBudget > 0 && displayedCoaches.length > 0
        ? Math.floor(rowViewportBudget / displayedCoaches.length)
        : densityRange.maxRowHeight;
    const rowHeight = Math.max(
        densityRange.minRowHeight,
        Math.min(densityRange.maxRowHeight, fittedRowHeight)
    );
    const eventInset = density === 'spacious' ? 5 : rowHeight <= 38 ? 2 : 3;
    const eventCardHeight = rowHeight - eventInset * 2;
    // Two concurrent lessons are more useful as two directly visible cells than
    // as one card plus an overflow badge. Dense rosters use a compact one-line
    // card for each lane, so the row height can stay unchanged.
    const visibleForegroundLanes = 2;
    const eventLaneGap = rowHeight <= 38 ? 1 : 3;
    const layouts = useMemo(
        () =>
            displayedCoaches.map((coach) => {
                const coachEvents = events.filter((event) => (event.coachId || UNASSIGNED_COACH_ID) === coach.id);
                const offEvents = coachEvents.filter((event) => event.kind === 'off');
                const foregroundEvents = coachEvents.filter((event) => event.kind !== 'off');
                return {
                    coachEvents,
                    offLayout: layoutOverlaps(offEvents, date, startHour, endHour, 1),
                    layout: layoutOverlaps(foregroundEvents, date, startHour, endHour, visibleForegroundLanes),
                };
            }),
        [date, displayedCoaches, endHour, events, startHour, visibleForegroundLanes]
    );
    const isToday = date.isSame(now, 'day');
    const nowMinute = now.hour() * 60 + now.minute();
    const showNow = isToday && nowMinute >= startHour * 60 && nowMinute <= endHour * 60;
    const dragValidation = drag ? validateQuickMoveDraft(drag.source, drag.draft) : null;
    const dragOverlaps = drag && dragValidation?.ok ? findQuickMoveOverlaps(drag.source, dragValidation, availabilityEvents) : [];

    useLayoutEffect(() => {
        const scrollElement = scrollRef.current;
        if (!scrollElement) return undefined;

        let frame = 0;
        let railFrame = 0;
        const updateRailBounds = () => {
            const scrollRect = scrollElement.getBoundingClientRect();
            const nextRailBounds = {
                left: Math.max(0, Math.floor(scrollRect.left)),
                width: Math.floor(scrollElement.clientWidth),
            };
            setBottomRailBounds((current) =>
                current.left === nextRailBounds.left && current.width === nextRailBounds.width
                    ? current
                    : nextRailBounds
            );
        };
        const applyMeasurement = () => {
            const measuredWidth = Math.floor(scrollElement.clientWidth);
            if (measuredWidth > 0) {
                setViewportWidth((current) => current === measuredWidth ? current : measuredWidth);
            }
            const measuredScrollMax = Math.max(0, Math.ceil(scrollElement.scrollWidth - measuredWidth));
            setHorizontalScrollMax((current) => current === measuredScrollMax ? current : measuredScrollMax);
            updateRailBounds();
            if (measuredScrollMax <= 1) {
                scrollElement.scrollLeft = 0;
                if (headerScrollRef.current) headerScrollRef.current.scrollLeft = 0;
                setHorizontalScrollLeft(0);
            } else {
                setHorizontalScrollLeft((current) => Math.min(current, measuredScrollMax));
            }
            const tableBodyTop = scrollElement.getBoundingClientRect().top;
            const footerReserve = density === 'dense' ? 10 : 34;
            const measuredRowBudget = Math.max(
                0,
                Math.floor(window.innerHeight - Math.max(0, tableBodyTop) - footerReserve)
            );
            setRowViewportBudget((current) =>
                current === measuredRowBudget ? current : measuredRowBudget
            );
        };
        const scheduleMeasurement = () => {
            window.cancelAnimationFrame(frame);
            frame = window.requestAnimationFrame(applyMeasurement);
        };
        const scheduleRailBounds = () => {
            window.cancelAnimationFrame(railFrame);
            railFrame = window.requestAnimationFrame(updateRailBounds);
        };

        // Measure before the first paint so rows do not flash at their maximum height.
        applyMeasurement();
        window.addEventListener('resize', scheduleMeasurement);
        window.addEventListener('scroll', scheduleRailBounds, true);
        if (typeof ResizeObserver !== 'undefined') {
            const observer = new ResizeObserver(scheduleMeasurement);
            observer.observe(scrollElement);
            return () => {
                window.cancelAnimationFrame(frame);
                window.cancelAnimationFrame(railFrame);
                observer.disconnect();
                window.removeEventListener('resize', scheduleMeasurement);
                window.removeEventListener('scroll', scheduleRailBounds, true);
            };
        }

        return () => {
            window.cancelAnimationFrame(frame);
            window.cancelAnimationFrame(railFrame);
            window.removeEventListener('resize', scheduleMeasurement);
            window.removeEventListener('scroll', scheduleRailBounds, true);
        };
    }, [boardWidth, density]);

    useEffect(() => {
        const frame = window.requestAnimationFrame(() => {
            const scrollElement = scrollRef.current;
            const headerScrollElement = headerScrollRef.current;
            if (!scrollElement) return;
            if (scrollElement.scrollWidth <= scrollElement.clientWidth) {
                scrollElement.scrollLeft = 0;
                if (headerScrollElement) headerScrollElement.scrollLeft = 0;
                setHorizontalScrollLeft(0);
                return;
            }
            const current = toSmartScheduleKst(new Date());
            const targetMinute = date.isSame(current, 'day')
                ? current.hour() * 60 + current.minute()
                : Math.max(startHour * 60, 6 * 60);
            const usableWidth = Math.max(300, scrollElement.clientWidth - coachColumnWidth);
            const targetLeft = (targetMinute - startHour * 60) * pixelsPerMinute - usableWidth / 2;
            const nextScrollLeft = Math.max(
                0,
                Math.min(scrollElement.scrollWidth - scrollElement.clientWidth, targetLeft)
            );
            scrollElement.scrollLeft = nextScrollLeft;
            if (headerScrollElement) headerScrollElement.scrollLeft = nextScrollLeft;
            setHorizontalScrollLeft(nextScrollLeft);
        });
        return () => window.cancelAnimationFrame(frame);
    }, [coachColumnWidth, date, pixelsPerMinute, startHour, timelineWidth]);

    const syncHorizontalScroll = (
        source: HTMLDivElement,
        ...targets: Array<HTMLDivElement | null>
    ) => {
        targets.forEach((target) => {
            if (!target || Math.abs(target.scrollLeft - source.scrollLeft) <= 0.5) return;
            target.scrollLeft = source.scrollLeft;
        });
        setHorizontalScrollLeft(source.scrollLeft);
    };

    const moveHorizontalScroll = (nextScrollLeft: number) => {
        const clamped = Math.max(0, Math.min(horizontalScrollMax, nextScrollLeft));
        setHorizontalScrollLeft(clamped);
        if (scrollRef.current) scrollRef.current.scrollLeft = clamped;
        if (headerScrollRef.current) headerScrollRef.current.scrollLeft = clamped;
    };

    const moveSlotFocus = (event: KeyboardEvent<HTMLButtonElement>) => {
        if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const buttons = Array.from(
            event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('.smart-schedule__empty-slot') || []
        );
        if (buttons.length === 0) return;
        const currentIndex = buttons.indexOf(event.currentTarget);
        const nextIndex = event.key === 'Home'
            ? 0
            : event.key === 'End'
            ? buttons.length - 1
            : Math.max(0, Math.min(buttons.length - 1, currentIndex + (event.key === 'ArrowRight' ? 1 : -1)));
        event.preventDefault();
        buttons[nextIndex]?.focus();
    };

    const beginDrag = (pointer: React.PointerEvent<HTMLDivElement>) => {
        if (!quickMove || pointer.button !== 0 || pointer.isPrimary === false || dragRef.current) return;
        const target = pointer.target as HTMLElement;
        if (target.closest('.quick-move-entry')) return;
        const card = target.closest<HTMLElement>('button[data-quick-move-id]');
        const timeline = card?.closest<HTMLElement>('.smart-schedule__coach-timeline');
        const scroll = scrollRef.current;
        const event = events.find((item) => item.id === card?.dataset.quickMoveId);
        if (!card || !timeline || !scroll || !event) return;
        const source = quickMove.getDragSource(event);
        if (!source) return;
        const rect = timeline.getBoundingClientRect();
        const startMinute = getEventStart(event).diff(date, 'minute', true);
        dragRef.current = {
            event, source, pointerId: pointer.pointerId, anchor: card,
            clientX: pointer.clientX, clientY: pointer.clientY,
            contentX: pointer.clientX - scroll.getBoundingClientRect().left + scroll.scrollLeft - coachColumnWidth,
            startMinute, rowTop: rect.top, rowBottom: rect.bottom,
            laneTop: card.getBoundingClientRect().top - rect.top, active: false,
            draft: quickMove.moveDraft(source, startMinute, date.format('YYYY-MM-DD')), outsideRow: false,
        };
        card.setPointerCapture?.(pointer.pointerId);
    };

    useEffect(() => {
        const cursorRoot = document.documentElement;
        const move = (pointer: PointerEvent) => {
            const current = dragRef.current;
            const scroll = scrollRef.current;
            if (!current || !quickMove || !scroll || pointer.pointerId !== current.pointerId) return;
            if (!current.active && Math.hypot(pointer.clientX - current.clientX, pointer.clientY - current.clientY) < 6) return;
            if (!current.active) {
                preview.suspend();
                cursorRoot.classList.add('quick-move-is-dragging');
            }
            current.active = true;
            pointer.preventDefault();
            const contentX = pointer.clientX - scroll.getBoundingClientRect().left + scroll.scrollLeft - coachColumnWidth;
            const targetMinute = current.startMinute + (contentX - current.contentX) / pixelsPerMinute;
            current.draft = quickMove.moveDraft(current.source, Math.max(0, Math.min(1430, targetMinute)), date.format('YYYY-MM-DD'));
            current.outsideRow = pointer.clientY < current.rowTop || pointer.clientY > current.rowBottom;
            setDrag({ eventId: current.event.id, coachId: current.source.coachId, source: current.source,
                draft: current.draft, top: current.laneTop, outsideRow: current.outsideRow });
        };
        const finish = (pointer: PointerEvent) => {
            const current = dragRef.current;
            if (!current || pointer.pointerId !== current.pointerId) return;
            if (current.active && pointer.type === 'pointerup') move(pointer);
            cursorRoot.classList.remove('quick-move-is-dragging');
            if (current.anchor.hasPointerCapture?.(pointer.pointerId)) current.anchor.releasePointerCapture?.(pointer.pointerId);
            dragRef.current = null;
            setDrag(null);
            if (!current.active) return;
            preview.resume();
            suppressedClick.current = { id: current.event.id, until: Date.now() + 500 };
            if (pointer.type === 'pointercancel') return;
            if (current.outsideRow || pointer.clientY < current.rowTop || pointer.clientY > current.rowBottom) {
                setDragNotice('다른 강사로 옮기기는 아직 지원하지 않아요');
                return;
            }
            quickMove?.openConfirm(current.source, current.draft);
        };
        const cancel = (event: globalThis.KeyboardEvent) => {
            if (event.key !== 'Escape' || !dragRef.current) return;
            const current = dragRef.current;
            cursorRoot.classList.remove('quick-move-is-dragging');
            suppressedClick.current = { id: current.event.id, until: Date.now() + 500 };
            if (current.anchor.hasPointerCapture?.(current.pointerId)) current.anchor.releasePointerCapture?.(current.pointerId);
            dragRef.current = null;
            setDrag(null);
            preview.resume();
        };
        window.addEventListener('pointermove', move, { passive: false });
        window.addEventListener('pointerup', finish);
        window.addEventListener('pointercancel', finish);
        window.addEventListener('keydown', cancel);
        return () => {
            cursorRoot.classList.remove('quick-move-is-dragging');
            const current = dragRef.current;
            if (current) {
                if (current.active) preview.resume();
                if (current.active) suppressedClick.current = { id: current.event.id, until: Date.now() + 500 };
                if (current.anchor.hasPointerCapture?.(current.pointerId)) current.anchor.releasePointerCapture?.(current.pointerId);
            }
            dragRef.current = null;
            setDrag(null);
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', finish);
            window.removeEventListener('pointercancel', finish);
            window.removeEventListener('keydown', cancel);
        };
    }, [coachColumnWidth, date, pixelsPerMinute, preview, quickMove]);

    useEffect(() => {
        if (!dragNotice) return;
        const timer = window.setTimeout(() => setDragNotice(''), 3500);
        return () => window.clearTimeout(timer);
    }, [dragNotice]);

    return (
        <section
            className={`smart-schedule__day smart-schedule__day--${density}${
                hasHorizontalOverflow ? ' smart-schedule__day--horizontal-overflow' : ''
            }`}
            aria-label={`${date.format('M월 D일')} 강사별 일간 일정`}
            data-coach-count={displayedCoaches.length}
        >
            {(drag?.outsideRow || dragNotice) && <div className="quick-move-drag-notice" role="status">다른 강사로 옮기기는 아직 지원하지 않아요</div>}
            <div className="smart-schedule__day-table" role="table" aria-label="강사별 일간 일정표">
                <div
                    ref={headerScrollRef}
                    className="smart-schedule__day-header-scroll"
                    role="rowgroup"
                    onScroll={(event) => syncHorizontalScroll(
                        event.currentTarget,
                        scrollRef.current
                    )}
                >
                    <div
                        className="smart-schedule__day-header"
                        role="row"
                        style={{
                            width: boardWidth,
                            '--coach-column-width': `${coachColumnWidth}px`,
                        } as CSSProperties}
                    >
                        <div className="smart-schedule__coach-heading" role="columnheader">강사</div>
                        <div className="smart-schedule__day-time-header" role="presentation" style={{ width: timelineWidth }}>
                            {hourTicks.map((hour) => (
                                <div
                                    key={hour}
                                    role="columnheader"
                                    className={showNow && hour === now.hour() ? 'is-current-hour' : undefined}
                                    style={{ width: hourWidth }}
                                    aria-current={showNow && hour === now.hour() ? 'time' : undefined}
                                    aria-label={showNow && hour === now.hour()
                                        ? `${hour}시 시간대, 현재 ${now.format('HH:mm')}`
                                        : undefined}
                                >
                                    <span>{String(hour).padStart(2, '0')}:00</span>
                                    {showNow && hour === now.hour() && <em>현재</em>}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div
                    ref={scrollRef}
                    className="smart-schedule__day-scroll"
                    role="rowgroup"
                    aria-label="강사별 일정, 가로로 스크롤할 수 있습니다"
                    tabIndex={0}
                    onScroll={(event) => syncHorizontalScroll(
                        event.currentTarget,
                        headerScrollRef.current
                    )}
                >
                    <div
                    className="smart-schedule__day-board"
                    role="presentation"
                    onPointerDownCapture={beginDrag}
                    onClickCapture={(click) => {
                        const card = (click.target as HTMLElement).closest<HTMLElement>('[data-quick-move-id]');
                        const suppressed = suppressedClick.current;
                        if (suppressed && suppressed.id === card?.dataset.quickMoveId && Date.now() < suppressed.until) {
                            click.preventDefault(); click.stopPropagation(); suppressedClick.current = null;
                        }
                    }}
                    style={{
                        width: boardWidth,
                        '--coach-column-width': `${coachColumnWidth}px`,
                    } as CSSProperties}
                >
                    <div role="presentation">
                        {displayedCoaches.map((coach, coachIndex) => {
                            const { coachEvents, layout, offLayout } = layouts[coachIndex];
                            const coachAvailabilityEvents = availabilityEvents.filter(
                                (event) => (event.coachId || UNASSIGNED_COACH_ID) === coach.id
                            );
                            const coachColor = getCoachColor(
                                coach.id === UNASSIGNED_COACH_ID ? undefined : coach.id
                            );
                            const availableSlots = slotStarts.filter((slotMinute) =>
                                isScheduleSlotFree(coachAvailabilityEvents, date, slotMinute)
                            );
                            const preferredMinute = date.isSame(now, 'day')
                                ? Math.ceil(nowMinute / 30) * 30
                                : Math.max(startHour * 60, 9 * 60);
                            const initialKeyboardSlot = availableSlots.find((slotMinute) => slotMinute >= preferredMinute)
                                ?? availableSlots[0];
                            const coachScheduleCount = coachEvents.reduce(
                                (count, event) => count + (event.kind === 'off' ? 0 : 1),
                                0
                            );
                            return (
                                <div
                                    key={coach.id}
                                    className="smart-schedule__coach-row"
                                    role="row"
                                    style={{ height: rowHeight }}
                                >
                                    <div className="smart-schedule__coach-cell" role="rowheader">
                                        <span
                                            className="smart-schedule__coach-avatar"
                                            style={{ background: coachColor.background, color: coachColor.foreground, borderColor: coachColor.border }}
                                            aria-hidden="true"
                                        >
                                            {coach.name.slice(0, 1)}
                                        </span>
                                        <span>
                                            <strong>{coach.name}</strong>
                                            <small>{coachScheduleCount > 0 ? `${coachScheduleCount}개 일정` : '일정 없음'}</small>
                                        </span>
                                    </div>

                                    <div
                                        className="smart-schedule__coach-timeline"
                                        role="cell"
                                        style={{
                                            width: timelineWidth,
                                            '--hour-width': `${hourWidth}px`,
                                            '--half-hour-width': `${hourWidth / 2}px`,
                                        } as CSSProperties}
                                    >
                                        {drag?.coachId === coach.id && <div className={`quick-move-drag-ghost${dragOverlaps.length ? ' is-overlapping' : ''}`} role="status" style={{
                                            left: (drag.draft.startMinutes - startHour * 60) * pixelsPerMinute,
                                            top: drag.top,
                                            width: (drag.draft.endMinutes - drag.draft.startMinutes) * pixelsPerMinute,
                                        }}>{drag.source.title} {quickMove?.formatClock(drag.draft.startMinutes)}→{quickMove?.formatClock(drag.draft.endMinutes)}</div>}
                                        {availableSlots.map((slotMinute) => {
                                            const startTime = formatSlotTime(slotMinute);
                                            return (
                                                <button
                                                    key={slotMinute}
                                                    type="button"
                                                    className="smart-schedule__empty-slot"
                                                    data-time={startTime}
                                                    tabIndex={slotMinute === initialKeyboardSlot ? 0 : -1}
                                                    style={{
                                                        left: (slotMinute - startHour * 60) * pixelsPerMinute,
                                                        width: hourWidth / 2,
                                                    }}
                                                    aria-label={`${coach.name}, ${date.format('M월 D일')} ${startTime} 일정 등록`}
                                                    title={`${startTime} 일정 등록`}
                                                    onKeyDown={moveSlotFocus}
                                                    onClick={() => onCreateSlot({
                                                        date: date.format('YYYY-MM-DD'),
                                                        startTime,
                                                        coachId: coach.id === UNASSIGNED_COACH_ID ? undefined : coach.id,
                                                        coachName: coach.id === UNASSIGNED_COACH_ID ? undefined : coach.name,
                                                    })}
                                                />
                                            );
                                        })}

                                        {offLayout.visible.map((item) => {
                                            const left = (item.startMinute - startHour * 60) * pixelsPerMinute;
                                            const width = Math.max(3, (item.endMinute - item.startMinute) * pixelsPerMinute - 2);
                                            const visibleStart = formatSlotTime(item.startMinute);
                                            const visibleEnd = formatSlotTime(item.endMinute);
                                            return (
                                                <EventCard
                                                    key={item.event.id}
                                                    event={item.event}
                                                    variant="day"
                                                    className={[
                                                        'is-background-off',
                                                        width < 34 ? 'is-tight' : '',
                                                    ].filter(Boolean).join(' ')}
                                                    displayTime={width >= 100 ? `${visibleStart}–${visibleEnd}` : visibleStart}
                                                    continuesFromPreviousDay={getEventStart(item.event).isBefore(date)}
                                                    status={getStatusGroup(item.event, now)}
                                                    onOpen={onOpenEvent}
                                                    style={{
                                                        left,
                                                        width,
                                                        top: eventInset,
                                                        height: eventCardHeight,
                                                    }}
                                                />
                                            );
                                        })}

                                        {layout.visible.map((item) => {
                                            const left = (item.startMinute - startHour * 60) * pixelsPerMinute;
                                            const width = Math.max(3, (item.endMinute - item.startMinute) * pixelsPerMinute - 2);
                                            const visibleStart = formatSlotTime(item.startMinute);
                                            const visibleEnd = formatSlotTime(item.endMinute);
                                            const laneHeight = Math.max(
                                                12,
                                                (eventCardHeight - Math.max(0, item.laneCount - 1) * eventLaneGap) / item.laneCount
                                            );
                                            const dayDetailLevel: 1 | 2 | 3 = width < 34 || laneHeight < 27
                                                ? 1
                                                : item.laneCount === 1 && width >= 48 && laneHeight >= 48
                                                ? 3
                                                : 2;
                                            return (
                                                <EventCard
                                                    key={item.event.id}
                                                    event={item.event}
                                                    variant="day"
                                                    className={[
                                                        width < 34 ? 'is-tight' : '',
                                                        width < 72 ? 'is-narrow' : '',
                                                        item.laneCount > 1 ? 'is-stacked' : '',
                                                    ].filter(Boolean).join(' ')}
                                                    displayTime={width >= 100 && item.laneCount === 1
                                                        ? `${visibleStart}–${visibleEnd}`
                                                        : visibleStart}
                                                    dayDetailLevel={dayDetailLevel}
                                                    continuesFromPreviousDay={getEventStart(item.event).isBefore(date)}
                                                    status={getStatusGroup(item.event, now)}
                                                    onOpen={onOpenEvent}
                                                    style={{
                                                        left,
                                                        width,
                                                        top: eventInset + item.lane * (laneHeight + eventLaneGap),
                                                        height: laneHeight,
                                                    }}
                                                />
                                            );
                                        })}

                                        {layout.hidden.map((overlap, overlapIndex) => (
                                            <button
                                                key={`${overlap.startMinute}-${overlapIndex}`}
                                                type="button"
                                                className="smart-schedule__overlap-badge smart-schedule__overlap-badge--day"
                                                style={{
                                                    left: Math.min(
                                                        Math.max(
                                                            2,
                                                            (overlap.endMinute - startHour * 60) * pixelsPerMinute - 30
                                                        ),
                                                        Math.max(2, timelineWidth - 32)
                                                    ),
                                                    top: 2,
                                                }}
                                                aria-label={`${coach.name} 겹쳐 숨겨진 일정 ${overlap.count}개 보기`}
                                                onClick={() => setAgenda({
                                                    title: `${coach.name} 겹친 일정`,
                                                    events: overlap.events,
                                                })}
                                            >
                                                +{overlap.count}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    {displayedCoaches.length === 0 && (
                        <div className="smart-schedule__day-no-coach">표시할 강사가 없습니다.</div>
                    )}
                    </div>
                </div>
                <div
                    className="smart-schedule__day-bottom-scroll"
                    role="region"
                    aria-label="일간 일정 하단 가로 스크롤"
                    aria-hidden={!hasHorizontalOverflow}
                    hidden={!hasHorizontalOverflow}
                    style={{
                        left: bottomRailBounds.left,
                        width: bottomRailBounds.width,
                    }}
                >
                    <input
                        className="smart-schedule__day-bottom-range"
                        type="range"
                        min={0}
                        max={Math.max(1, horizontalScrollMax)}
                        step={1}
                        value={Math.min(horizontalScrollLeft, horizontalScrollMax)}
                        aria-label="일간 일정 가로 위치"
                        aria-valuetext={`${Math.round(
                            horizontalScrollMax > 0 ? (horizontalScrollLeft / horizontalScrollMax) * 100 : 0
                        )}% 지점`}
                        onKeyDown={(event) => {
                            const keyboardStep = Math.max(24, Math.round(hourWidth / 2));
                            const pageStep = Math.max(keyboardStep, Math.round(viewportWidth * 0.8));
                            const nextScrollLeft = event.key === 'Home'
                                ? 0
                                : event.key === 'End'
                                ? horizontalScrollMax
                                : event.key === 'ArrowLeft'
                                ? horizontalScrollLeft - keyboardStep
                                : event.key === 'ArrowRight'
                                ? horizontalScrollLeft + keyboardStep
                                : event.key === 'PageUp'
                                ? horizontalScrollLeft - pageStep
                                : event.key === 'PageDown'
                                ? horizontalScrollLeft + pageStep
                                : null;
                            if (nextScrollLeft === null) return;
                            event.preventDefault();
                            moveHorizontalScroll(nextScrollLeft);
                        }}
                        onChange={(event) => moveHorizontalScroll(Number(event.currentTarget.value))}
                        style={{
                            '--day-scroll-thumb-width': `${bottomRailThumbWidth}px`,
                        } as CSSProperties}
                    />
                </div>
            </div>
            {density !== 'dense' && (
                <p className="smart-schedule__day-help">
                    빈 칸을 선택하면 강사와 시간이 입력된 상태로 일정을 등록할 수 있어요.
                </p>
            )}
            {agenda && (
                <AgendaPanel
                    title={agenda.title}
                    events={agenda.events}
                    now={now}
                    onClose={() => setAgenda(null)}
                    onOpenEvent={onOpenEvent}
                />
            )}
        </section>
    );
};

export default React.memo(DayView);
